import Entitlement from './Entitlement.model'
import Collection from '@/core/classes/Collection'
import { InvalidEntitlement, Unauthorized } from '../errors/Entitlement.errors'

export default class Entitlements extends Collection {
  constructor (entitlements, isAuthenticated = true) {
    if (isAuthenticated) {
      if (!(entitlements instanceof Object && entitlements.constructor !== Array && Object.keys(entitlements).length !== 0)) {
        throw new InvalidEntitlement({ entitlements })
      }
    }

    super(
      entitlements
        ? Entitlements.$entitlementsMutator(entitlements)
        : []
    )
  }

  /**
   * Entitlement collection model
   *
   * @return {Entitlement}
   */
  model () {
    return Entitlement
  }

  /**
   * Check if token pass all entitlements
   *
   * @param {Array<String>|String} entitlements
   * @param {Boolean} throwError false
   * @param {Boolean} strict true [must pass all entitlements]
   *
   * @return {Boolean}
   */
  can (entitlements, { throwError = false, strict = true } = {}) {
    Entitlements.normalizeEntitlements(entitlements)

    return Object.keys(entitlements).reduce((isPassed, entitlement) => {
      const entitlementModel = this.find(entitlement) ||
        this.find('*') ||
        false

      if (!entitlementModel) {
        if (throwError) {
          throw new Unauthorized({ entitlementName: entitlement })
        } else {
          return strict ? false : isPassed
        }
      }

      return strict
        ? entitlementModel.can(entitlements[entitlement], { throwError, strict }) && isPassed
        : entitlementModel.can(entitlements[entitlement], { throwError, strict }) || isPassed
    }, strict)
  }

  /**
   * Check if token pass any entitlements
   *
   * @param {Array<String>|String} entitlements
   * @param {Boolean} throwError false
   *
   * @return {Boolean}
   */
  anyCan (entitlements, { throwError = false } = {}) {
    return this.can(entitlements, { throwError, strict: false })
  }

  /**
   * Normalize provided entitlements object
   *
   * - Validate entitlements type
   * - Convert actions into array
   *
   * @param {Object} entitlements
   *
   * @return {Object}
   */
  static normalizeEntitlements (entitlements) {
    Entitlements.validateEntitlements(entitlements)

    Object.keys(entitlements).forEach(entitlement => {
      if (typeof entitlements[entitlement] === 'string') {
        entitlements[entitlement] = entitlements[entitlement].split(',')
      }
    })

    return entitlements
  }

  /**
   * Validate entitlements data structure
   *
   * @param {Array<String>|String} entitlements
   *
   * @return {void|Error<InvalidEntitlement>}
   */
  static validateEntitlements (entitlements) {
    if (!(entitlements instanceof Object && entitlements.constructor !== Array && Object.keys(entitlements).length !== 0)) {
      throw new InvalidEntitlement({ entitlements, message: 'Entitlements must be provided as an object' })
    } else if (!Object.keys(entitlements).reduce((validActions, entitlement) => validActions && ((typeof entitlements[entitlement] === 'string' && entitlements[entitlement]) || (entitlements[entitlement] && entitlements[entitlement].constructor === Array)), true)) {
      throw new InvalidEntitlement({ entitlements, message: 'Entitlement actions must be string or array' })
    }
  }

  /**
   * Mutate entitlements from api response
   *
   * @param {Object} entitlement
   *
   * @return {Object[]|Error<InvalidEntitlement>}
   */
  static $entitlementsMutator (entitlements) {
    return Object.keys(entitlements).map(entitlement => {
      return { id: entitlement, actions: entitlements[entitlement].split(',') }
    })
  }

  /**
   * Get all keys that passed can entitlements as an array
   *
   * @param {Object} checks
   *
   * @return {Array}
   */
  getPassed (checks) {
    return Object.keys(checks).filter(check => this.can(checks[check]))
  }

  /**
   * Get all keys that passed anyCan entitlements as an array
   *
   * @param {Object} checks
   *
   * @return {Array}
   */
  getAnyPassed (checks) {
    return Object.keys(checks).filter(check => this.anyCan(checks[check]))
  }

  /**
   * Filter array of objects by $can/$anyCan property
   *
   * $can take priority that $anyCan if both exist
   *
   * @param {Object[]} items
   *
   * @return {Boolean}
   */
  filterByEntitlements (items) {
    return items.filter(item => (!('$can' in item || '$anyCan' in item)) || ('$can' in item ? this.can(item.$can) : this.anyCan(item.$anyCan)))
  }
}
